"use client";
import { useI18n } from "@/i18n/provider";
import { clientMessages, formatMessage } from "@/i18n/client-messages";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { apiRequest, errorMessage, type GamePage } from "@/lib/api";
import { CopyButton } from "./copy-button";
import { ExpandIcon, PlayIcon, RestartIcon } from "./icons";

export function GamePlayer({ game }: { game: GamePage }) {
  const { locale } = useI18n();
  const t = clientMessages[locale];
  const container = useRef<HTMLDivElement>(null);
  const [started, setStarted] = useState(false);
  const [revision, setRevision] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [wide, setWide] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    const update = () => setFullscreen(document.fullscreenElement === container.current);
    document.addEventListener("fullscreenchange", update);
    return () => document.removeEventListener("fullscreenchange", update);
  }, []);

  useEffect(() => {
    if (!started || loaded) return;
    const timer = window.setTimeout(() => {
      setNotice(t.playerWaiting);
    }, 15_000);
    return () => window.clearTimeout(timer);
  }, [started, loaded, revision, t.playerWaiting]);

  function play() {
    try {
      const source = new URL(game.iframe_url);
      if (!["http:", "https:"].includes(source.protocol) || source.origin === window.location.origin) {
        setNotice(t.invalidGameUrl);
        return;
      }
      setNotice(""); setLoaded(false); setStarted(true);
    } catch { setNotice(t.invalidGameUrl); }
  }

  async function expand() {
    if (document.fullscreenElement) { await document.exitFullscreen().catch(() => undefined); return; }
    try {
      if (!container.current?.requestFullscreen) throw new Error("Fullscreen is unavailable");
      await container.current.requestFullscreen();
    } catch { setWide((value) => !value); setNotice(t.wideFallback); }
  }

  return <>
    <div ref={container} className={`player-shell ${wide ? "player-wide" : ""}`}>
      <div className="player-stage">
        {started ? <iframe key={revision} src={game.iframe_url} title={formatMessage(t.gameFrameTitle, { title: game.title })} sandbox="allow-scripts allow-same-origin allow-pointer-lock" allow="fullscreen; gamepad" allowFullScreen referrerPolicy="no-referrer" onLoad={() => { setLoaded(true); setNotice(""); }} /> : <div className="player-start"><span className="player-eyebrow">{t.readyWhenYouAre}</span><h2>{game.title}</h2><button className="play-button" onClick={play}><PlayIcon />{t.play}</button><p>{t.noDownload}</p></div>}
      </div>
      <div className="player-toolbar"><span className="player-status"><span className={`small-dot ${started ? "active-dot" : ""}`} />{started ? loaded ? t.gameFrameOpen : t.loadingGameFiles : t.browserGame}</span><div><button className="toolbar-button" onClick={() => { setLoaded(false); setNotice(""); setRevision((value) => value + 1); }} disabled={!started}><RestartIcon /><span>{t.restart}</span></button><button className="toolbar-button" onClick={() => void expand()}><ExpandIcon /><span>{fullscreen ? t.exitFullscreen : t.fullscreen}</span></button></div></div>
    </div>
    {notice ? <p className="player-notice" role="status">{notice}</p> : null}
  </>;
}

export function GameActions({ game }: { game: GamePage }) {
  const { locale } = useI18n();
  const t = clientMessages[locale];
  const [reportOpen, setReportOpen] = useState(false);
  const [reason, setReason] = useState("broken");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  async function report(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError("");
    try { await apiRequest<{ received: boolean }>(`/public/games/${encodeURIComponent(game.id)}/reports`, { method: "POST", body: JSON.stringify({ reason, ...(details.trim() ? { details: details.trim() } : {}) }) }); setSent(true); setDetails(""); }
    catch (cause) { setError(errorMessage(cause, locale)); }
    finally { setBusy(false); }
  }

  return <div className="game-actions"><div className="share-row"><CopyButton value={game.play_url} label={t.copyPlayLink} /><button className="text-button muted-button" onClick={() => setReportOpen((value) => !value)} aria-expanded={reportOpen} aria-controls="game-report">{t.reportProblem}</button></div>{reportOpen ? <section className="report-panel" id="game-report" aria-labelledby="report-title"><h3 id="report-title">{t.reportTitle}</h3>{sent ? <p className="inline-notice notice-success" role="status">{t.reportSent}</p> : <form className="stack-form" onSubmit={(event) => void report(event)}><label htmlFor="report-reason">{t.reportReason}</label><select id="report-reason" value={reason} onChange={(event) => setReason(event.target.value)} disabled={busy}><option value="broken">{t.reportBroken}</option><option value="inappropriate">{t.reportInappropriate}</option><option value="other">{t.reportOther}</option></select><label htmlFor="report-details">{t.details} <span className="field-optional">{t.optional}</span></label><textarea id="report-details" maxLength={2000} rows={3} value={details} onChange={(event) => setDetails(event.target.value)} placeholder={t.reportPlaceholder} disabled={busy} />{error ? <p className="form-error" role="alert">{error}</p> : null}<div className="form-actions"><button className="button button-dark button-small" type="submit" disabled={busy}>{busy ? t.sending : t.sendReport}</button><button className="text-button" type="button" onClick={() => setReportOpen(false)}>{t.cancel}</button></div></form>}</section> : null}</div>;
}
